import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import VideoGuide from '../../../components/common/VideoGuide'
import { DisplayNameDetail, RequestPatchTaskCompanyServiceName, ResponseGetTaskCompanyServiceName } from '../../../../states/types/task'
import {
  fetchTaskCompanyServiceName,
  fetchTaskCompanyServiceNameEdit,
  fetchTaskLabelingTargetModeSetting,
  updateTaskCompanyServiceName,
  updateTaskLabelingTargetModeSetting,
} from '../../../../states/containers/task/taskSlice'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import EditTargetApplicationModal from '../../../components/task/EditTargetApplicationModal'
import { useAlert } from 'react-alert'
import { compile } from '../../../../utils/search'
import { updateTaskTargetSettingDisplayType } from '../../../../states/containers/common/currentSlice'
import { format, secToHour, secToMinute, secToSec } from '../../../../utils/date'

const SORT_TYPE = [
  {
    type: "time_desc",
    label: "直近一週間の使用時間順",
  },
  {
    type: "new",
    label: "新着順",
  },
  {
    type: "old",
    label: "登録順",
  },
  {
    type: "app_asc",
    label: "アプリ名昇順",
  },
  {
    type: "app_desc",
    label: "アプリ名降順",
  },
]

const SLICE_LIMIT = 100

const Index: FC = () => {
  const {
    task,
    current,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()
  const alert = useAlert()

  const initDisplayNameDetail: DisplayNameDetail = {
    display_name: "data",
    labeling_target: false,
    icon: "fas fa-desktop",
    color: "#333333",
    created_at: "",
    time: 0,
    named: false,
    company_service_names: [
      {
        id: 1,
        service_type: 'application',
        raw_name: "data",
      },
    ],
  }

  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [data, setData] = useState<ResponseGetTaskCompanyServiceName>({ display_names: [] })
  const [initData, setInitData] = useState<ResponseGetTaskCompanyServiceName>({ display_names: [] })
  const [filterType, setFilterType] = useState<string>("all")
  const [isDisplayEditModal, setIsDisplayEditModal] = useState<boolean>(false)
  const [multiCheckList, setMultiCheckList] = useState<string[]>([])
  const [displayNameDetails, setDisplayNameDetails] = useState<DisplayNameDetail[]>([initDisplayNameDetail])
  const [searchWord, setSearchWord] = useState<string>("")
  const [windowTitles, setWindowTitles] = useState<string[]>([])
  const [sortType, setSortType] = useState<string>("time_desc")
  const [modified, setModified] = useState<string>("")

  const [sliceNumber, setSliceNumber] = useState<number>(0)

  const [isLabelingTargetModeLoading, setIsLabelingTargetModeLoading] = useState<boolean>(false)

  const slicedData = useMemo(() => {
    const length = Math.ceil(data.display_names.length / SLICE_LIMIT)
    return new Array(length).fill(null).map((_, i) => data.display_names.slice(i * SLICE_LIMIT, (i + 1) * SLICE_LIMIT))
  }, [data])

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = () => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if ((bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setSliceNumber((self) => self + 1)
    }
  }

  const toggleLabelingTarget = (companyServiceName: string, companyServiceNameIds: number[], isTarget: boolean) => {
    if (!isTarget && task.taskLabelingTargetModeSetting?.all_labeling_target) {
      dispatch(
        updateTaskLabelingTargetModeSetting(
          { all_labeling_target: false },
        ),
      )
    }
    dispatch(
      updateTaskCompanyServiceName(
        {
          company_service_name_ids: companyServiceNameIds,
          labeling_target: isTarget,
        },
        () => {
          let text = `[${companyServiceName}]を${isTarget ? "設定" : "解除"}`
          if (!isTarget && task.taskLabelingTargetModeSetting?.all_labeling_target) {
            text += "、また、仕分け対象アプリを手動設定に変更しました"
          } else {
            text += "しました"
          }
          alert.success(text)
          const changedValue = initData?.display_names.map((v) => (v.display_name === companyServiceName ? { ...v, labeling_target: isTarget } : v))
          if (changedValue) setInitData({ display_names: changedValue })
        },
      ),
    )
  }

  const editTargetApplication = (param: DisplayNameDetail[]) => {
    setDisplayNameDetails(param)
    setIsDisplayEditModal(true)
    if (param.map((v) => v.company_service_names).flat().length === 1) {
      dispatch(
        fetchTaskCompanyServiceNameEdit(
          { company_service_name_id: param.map((v) => v.company_service_names).flat()[0].id.toString() },
          (result) => {
            setWindowTitles(result.sample_window_titles)
          },
        ),
      )
    }
  }

  const modTargetService = (params: RequestPatchTaskCompanyServiceName) => {
    dispatch(
      updateTaskCompanyServiceName(
        params,
        () => {
          alert.success("変更が完了しました")
          dispatch(fetchTaskCompanyServiceName())
          setModified(params?.service_name || "")
          const targetEl = document.getElementById(`app_${params?.service_name || ""}`)
          targetEl?.scrollIntoView({ behavior: 'smooth', block: "center" })
          setTimeout(() => {
            setModified("")
          }, 5000)
        },
      ),
    )
  }

  const clickCheckbox = (displayName: string) => {
    if (multiCheckList.includes(displayName)) {
      setMultiCheckList(multiCheckList.filter((v) => v !== displayName))
    } else {
      setMultiCheckList([...multiCheckList, displayName])
    }
  }

  const changeMultiLabelingTarget = (isTarget: boolean) => {
    if (!isTarget && task.taskLabelingTargetModeSetting?.all_labeling_target) {
      dispatch(
        updateTaskLabelingTargetModeSetting(
          { all_labeling_target: false },
        ),
      )
    }
    const companyServiceNameIds = initData.display_names.filter((v) => multiCheckList.includes(v.display_name))
      .map((v) => v.company_service_names.map((w) => w.id)).flat()
    dispatch(
      updateTaskCompanyServiceName(
        {
          company_service_name_ids: companyServiceNameIds,
          labeling_target: isTarget,
        },
        () => {
          let text = "変更が完了"
          if (!isTarget && task.taskLabelingTargetModeSetting?.all_labeling_target) {
            text += "、また、仕分け対象アプリを手動設定に変更しました"
          } else {
            text += "しました"
          }
          alert.success(text)
          setMultiCheckList([])
          dispatch(fetchTaskCompanyServiceName())
        },
      ),
    )
  }

  const changeFilterType = (type: string) => {
    setFilterType(type)
    dispatch(updateTaskTargetSettingDisplayType(type))
  }

  const closeModal = () => {
    setIsDisplayEditModal(false)
    setWindowTitles([])
  }

  const toggleLabelingTargetMode = (targetMode: boolean) => {
    setIsLabelingTargetModeLoading(true)
    if (targetMode) {
      const companyServiceNameIds = initData.display_names.filter((v) => !v.labeling_target)
        .map((v) => v.company_service_names.map((w) => w.id)).flat()
      dispatch(
        updateTaskCompanyServiceName(
          {
            company_service_name_ids: companyServiceNameIds,
            labeling_target: true,
          },
          () => {
            setMultiCheckList([])
            dispatch(fetchTaskCompanyServiceName())
            setIsLabelingTargetModeLoading(false)
          },
        ),
      )
    }
    dispatch(
      updateTaskLabelingTargetModeSetting(
        { all_labeling_target: targetMode },
        () => {
          if (targetMode) {
            changeFilterType("all")
            alert.success("すべてのアプリケーションを仕分け対象アプリに設定して、仕分け対象アプリを自動設定に変更しました")
          } else {
            alert.success("仕分け対象アプリを手動設定に変更しました")
          }
          setIsLabelingTargetModeLoading(false)
        },
      ),
    )
  }

  const buildSecToTime = (sec: number) => (
    <>
      {
        !!Number(secToHour(sec)) && (
          <>
            {`${secToHour(sec)}時間`}
          </>
        )
      }
      {
        Number(secToMinute(sec)) ? (
          <>
            {`${secToMinute(sec)}分`}
          </>
        ) : (
          <>
            0分
          </>
        )
      }
      {
        !!Number(secToSec(sec)) && (
          <>
            {`${secToSec(sec)}秒`}
          </>
        )
      }
    </>
  )

  useEffect(() => {
    if (!data) return
    const word = searchWord.toLowerCase().split(/\s+/)
    let list = initData.display_names.filter((value) => {
      if (value === null) return false
      return (compile(word).test(value.display_name.toLowerCase()))
    })
    if (filterType === "target") {
      list = list.filter((v) => v.labeling_target)
    } else if (filterType === "no_target") {
      list = list.filter((v) => !v.labeling_target)
    }
    if (sortType === "new") {
      list = list.sort((a, b) => Number(format(b.created_at, "YYYYMMDDHms")) - Number(format(a.created_at, "YYYYMMDDHms")))
    } else if (sortType === "old") {
      list = list.sort((a, b) => Number(format(b.created_at, "YYYYMMDDHms")) - Number(format(a.created_at, "YYYYMMDDHms"))).reverse()
    } else if (sortType === "time_asc") {
      list = list.sort((a, b) => a.time - b.time)
    } else if (sortType === "time_desc") {
      list = list.sort((a, b) => a.time - b.time).reverse()
    } else if (sortType === "app_asc") {
      list = list.sort((a, b) => a.display_name.localeCompare(b.display_name, 'ja'))
    } else if (sortType === "app_desc") {
      list = list.sort((a, b) => a.display_name.localeCompare(b.display_name, 'ja')).reverse()
    }
    setData({ display_names: list })
    setMultiCheckList([])
    setSliceNumber(0)
  }, [filterType, searchWord, initData, sortType])

  useEffect(() => {
    dispatch(
      fetchTaskCompanyServiceName(
        () => {
          setPageLoading(false)
        },
      ),
    )
    dispatch(fetchTaskLabelingTargetModeSetting())
  }, [])

  useEffect(() => {
    if (!task.taskCompanyServiceName) return
    setInitData(task.taskCompanyServiceName as ResponseGetTaskCompanyServiceName)
  }, [task.taskCompanyServiceName])

  useEffect(() => {
    setFilterType(current.taskTargetSettingDisplayType)
  }, [current.taskTargetSettingDisplayType])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - 仕分け対象アプリ設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        仕分け対象アプリ設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          仕分け対象アプリ設定
          {/* <VideoGuide
            videoId="vpAXoNKucBE"
          /> */}
        </h1>
      </section>
      <section className="page_description">
        <p>
          仕分け対象アプリ設定を設定します。
        </p>
        <p>
          ここで設定したアプリケーションのデータが計測の対象になります。データを計測したいときは、対象になるアプリケーションを設定してください。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              pageLoading ? (
                <LoadingSquare>
                  <>
                    仕分け対象アプリ設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div
                    className="header_search_area"
                    style={{
                      border: "solid 1px #CCCCCC",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div className="detail">
                      すべてのアプリケーションを仕分け対象アプリに設定して、仕分け対象アプリを自動設定に変更します
                      {
                        isLabelingTargetModeLoading ? (
                          <FontAwesomeIcon icon={['fas', 'spinner']} spin style={{ fontSize: "24px" }} />
                        ) : (
                          <FontAwesomeIcon
                            icon={['fas', task.taskLabelingTargetModeSetting?.all_labeling_target ? 'toggle-on' : 'toggle-off']}
                            style={{
                              color: task.taskLabelingTargetModeSetting?.all_labeling_target ? "#008800" : "#666666",
                              cursor: "pointer",
                              fontSize: "24px",
                            }}
                            onClick={() => toggleLabelingTargetMode(!task.taskLabelingTargetModeSetting?.all_labeling_target)}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="header_search_area">
                    {
                      !task.taskLabelingTargetModeSetting?.all_labeling_target && (
                        <div className="detail">
                          表示:
                          <div className="toggle_area">
                            <button
                              type="button"
                              className={`${filterType === "all" ? "on" : "off"}`}
                              onClick={() => changeFilterType("all")}
                            >
                              すべて
                            </button>
                            <button
                              type="button"
                              className={`${filterType === "target" ? "on" : "off"}`}
                              onClick={() => changeFilterType("target")}
                            >
                              仕分け対象アプリのみ
                            </button>
                            <button
                              type="button"
                              className={`${filterType === "no_target" ? "on" : "off"}`}
                              onClick={() => changeFilterType("no_target")}
                            >
                              非仕分け対象アプリのみ
                            </button>
                          </div>
                        </div>
                      )
                    }
                    <div className="detail">
                      アプリケーション名検索:
                      <input
                        type="text"
                        value={searchWord}
                        onChange={(e) => setSearchWord(e.target.value)}
                      />
                      {
                        !!searchWord.length && (
                          <button
                            type="button"
                            className="reset"
                            onClick={() => setSearchWord("")}
                            style={{
                              position: "absolute",
                              right: "6px",
                              top: "50%",
                              fontSize: "10px",
                              padding: 0,
                              color: "#999999",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <FontAwesomeIcon icon={['far', 'times-circle']} />
                          </button>
                        )
                      }
                    </div>
                    <div className="detail">
                      並び順:
                      <select
                        onChange={(e) => setSortType(e.target.value)}
                        style={{
                          appearance: "auto",
                          border: "solid 1px #999999",
                          borderRadius: "4px",
                          padding: "6px 8px",
                        }}
                      >
                        {
                          SORT_TYPE.map((v) => (
                            <option
                              key={`sort_${v.type}`}
                              value={v.type}
                            >
                              {v.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  {
                    !!multiCheckList.length && (
                      <div className="header_multi_select_area">
                        <div className="summary_area">
                          <div className="num">
                            {multiCheckList.length}
                          </div>
                          件のアプリケーションが選択されています
                          <button
                            type="button"
                            onClick={() => setMultiCheckList([])}
                          >
                            選択解除
                          </button>
                        </div>
                        <div className="control_area">
                          選択したアプリケーションを
                          <div className="button_area">
                            <button
                              type="button"
                              onClick={() => editTargetApplication(initData.display_names.filter((v) => multiCheckList.includes(v.display_name)))}
                              className="update"
                            >
                              まとめて編集する
                            </button>
                            <button
                              type="button"
                              onClick={() => changeMultiLabelingTarget(true)}
                              className="target"
                            >
                              仕分け対象アプリに設定する
                            </button>
                            <button
                              type="button"
                              onClick={() => changeMultiLabelingTarget(false)}
                              className="no_target"
                            >
                              仕分け対象アプリから解除する
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  }
                  <div
                    style={{
                      color: "#4372ba",
                      fontSize: "14px",
                      fontWeight: "bold",
                      border: "dashed 1px #4372ba",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "16px",
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'info-circle']} />
                    これまで使用したことがあるアプリケーションが表示されています
                  </div>
                  <div className="company_service_name_table">
                    <div className="row_head">
                      <div>
                        <Tips
                          description="仕分け対象アプリを設定・解除します"
                          className=""
                        >
                          <>
                            対象
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="名称の変更や対象の変更をおこないます"
                          className=""
                        >
                          <>
                            編集
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="仕分け画面に表示されるアプリケーション名です"
                          className=""
                        >
                          <>
                            対象アプリケーション名
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="アプリケーションとして表示される対象のプロセス・ドメイン名一覧です"
                          className=""
                        >
                          <>
                            対象プロセス・ドメイン名
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="直近一週間で使用したアプリケーションの合計時間です"
                          className=""
                        >
                          <>
                            直近一週間の使用時間
                          </>
                        </Tips>
                      </div>
                    </div>
                    {
                      slicedData.filter((_w, i) => i <= sliceNumber).flat().map((v) => (
                        <div
                          className={`row ${modified === v.display_name && "modified"}`}
                          key={v.display_name}
                          id={`app_${v.display_name}`}
                          style={{
                            backgroundColor: multiCheckList.includes(v.display_name) ? "#f0fff8" : "#FFFFFF",
                          }}
                        >
                          <div>
                            <FontAwesomeIcon
                              icon={['fas', v.labeling_target ? 'toggle-on' : 'toggle-off']}
                              style={{
                                color: v.labeling_target ? "#008800" : "#666666",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleLabelingTarget(v.display_name, v.company_service_names.map((w) => w.id), !v.labeling_target)}
                            />
                          </div>
                          <div>
                            <FontAwesomeIcon
                              icon={['fas', 'cog']}
                              style={{
                                color: "#666666",
                                cursor: "pointer",
                              }}
                              onClick={() => editTargetApplication([v])}
                            />
                          </div>
                          <div
                            onClick={() => clickCheckbox(v.display_name)}
                            role="none"
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {
                                v.icon.split(' ').length === 2 && (
                                  <FontAwesomeIcon
                                    icon={[v.icon.split(' ')[0] as IconPrefix, v.icon.split(' ')[1] as IconName]}
                                    style={{
                                      color: v.color,
                                      fontSize: "16px",
                                    }}
                                    fixedWidth
                                  />
                                )
                              }
                              {v.display_name}
                              {
                                !v.named && (
                                  <Tips
                                    description="アプリケーション名を設定し忘れていませんか？"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'exclamation']}
                                        style={{
                                          color: "#ff0000",
                                          fontSize: "16px",
                                          cursor: "pointer",
                                        }}
                                        onClick={
                                          (e) => {
                                            editTargetApplication([v])
                                            e.stopPropagation()
                                          }
                                        }
                                      />
                                    </>
                                  </Tips>
                                )
                              }
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#666666",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              {
                                v.company_service_names.map((w) => (
                                  <div
                                    key={`${w.raw_name}_${w.service_type}`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: w.service_type === "application" ? "#ad0505" : "#067ecd",
                                        color: "#FFFFFF",
                                        borderRadius: "4px",
                                        fontSize: "10px",
                                        padding: "1px 4px",
                                        fontWeight: "initial",
                                      }}
                                    >
                                      {w.service_type === "application" ? "プロセス名" : "ドメイン名"}
                                    </div>
                                    {w.raw_name}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              {buildSecToTime(v.time)}
                            </div>
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "-30px",
                              fontSize: "18px",
                              cursor: "pointer",
                              transform: "translateY(-50%)",
                              border: "none",
                            }}
                            onClick={() => clickCheckbox(v.display_name)}
                            role="none"
                          >
                            <FontAwesomeIcon
                              icon={['far', multiCheckList.includes(v.display_name) ? 'check-square' : 'square']}
                              style={{ color: multiCheckList.includes(v.display_name) ? "#228b22" : "#666666" }}
                            />
                          </div>
                        </div>
                      ))
                    }
                    {
                      slicedData.length - 1 > sliceNumber && (
                        <>
                          <LoadingSquare>
                            <>
                              仕分け対象アプリデータを表示中…
                            </>
                          </LoadingSquare>
                        </>
                      )
                    }
                    {
                      !data.display_names.length && (
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#999999",
                            padding: "32px",
                          }}
                        >
                          該当のアプリケーションがありません
                        </div>
                      )
                    }
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <EditTargetApplicationModal
        appList={initData.display_names}
        details={displayNameDetails}
        closeCallback={closeModal}
        modCallback={(params) => modTargetService(params)}
        isDisplay={isDisplayEditModal}
        windowTitles={windowTitles}
      />
    </section>
  )
}

export default Index
